/**
 * Panel 1 — `Fixture List` (§A3).
 *
 * One row per fixture, with the three technical slots side by side. That shape is the whole
 * point of the page: the TD reads a match across, not down, and the three panels below it are
 * derived from exactly what is on these rows.
 *
 * What is on a row and what is deliberately not:
 *
 *  * **The names are the controls.** Clicking, Entering or Spacing on a name opens the §A6
 *    popover for that slot. There is no dropdown under the name any more: a Technical
 *    Delegate reported the old arrangement as slow to read and as not matching the design he
 *    signed off, and a name that looks like the thing you press should be the thing you press.
 *  * **A locked slot has no control at all** — it says why instead. §A4 rule 6 freezes a
 *    fixture TMS has marked Official; §A11 locks a match marked as the record TMS holds;
 *    CC-Q25 freezes a slot the TD has signed off. None draws a picker the API would refuse;
 *    pressing the name says the reason out loud.
 *  * **`Push` is absent, not disabled.** §A3 puts `Update`, `Check` and `Push` on the row.
 *    `Update` is the picker, `Check` is here, and there is no TMS write transport in this
 *    build — so rather than a button that would lie, the panel foot says so in one line.
 *  * **RUN 82 — the lock layer stands in for the push that is not here (§A11).** Rulings #14 and
 *    #15 (16 September 2026): Day 1 is keyed into TMS by hand, so a day must be lockable with no
 *    push at all, and a locked appointment is read-only and counts as the record of who
 *    officiated. `Mark Day Locked` sits beside `Confirm Day` on the head, because it is the step
 *    after it; `Unlock` sits on the row, because a correction is made one match at a time. Both
 *    only OPEN a question — the panel above asks it, and makes the write.
 *
 * U1, U2, RU and VU are not here and are not an oversight: they belong to the Umpires
 * Managers (§B1) and the API does not serve them on this route.
 */

import { Fragment, useRef } from "react";

import type { AppointmentWarning, TechnicalSlot } from "@hockeytd/shared";

import type {
  AppointmentFixtureBody,
  AppointmentSlotBody,
  OfficialView,
} from "../../api/types.js";
import {
  dayGroups,
  dayLabel,
  dayNumbers,
  useLandOnDay,
} from "../schedule/matchDays.js";
import { FixtureWarnings } from "./FixtureWarnings.js";
import { TipsFold } from "./TipsFold.js";
import {
  COMPETITION_TABS,
  STAGE_TABS,
  Tabs,
} from "./Tabs.js";
import type { CompetitionFilter, StageFilter } from "./derive.js";
import {
  REOPENED_WITHDRAW_REASON,
  exactTournamentInstant,
  fixtureSignOff,
  hasRuleBreach,
  needsRePushNote,
  ruleBreachReason,
  shortTournamentTime,
  slotLock,
  statusLabel,
  tournamentClock,
  withdrawLock,
} from "./vocabulary.js";

export interface FixtureListPanelProps {
  /** Already filtered to the panel's own tabs. */
  fixtures: readonly AppointmentFixtureBody[];
  /** The whole listing, so the empty state can say which kind of empty it is. */
  allFixtures: readonly AppointmentFixtureBody[];
  /**
   * RUN 37 — the day the list scrolls itself to on the first draw, or `null` for the top.
   *
   * Worked out above from the tournament's own clock; this panel only obeys it, and only
   * once. See `features/schedule/matchDays.ts`.
   */
  landOn?: string | null;
  /**
   * The day the page is APPOINTING, which is the day the Match Day dropdown names.
   *
   * RUN 37 widened the list to the whole schedule and made every row off this day read-only.
   * RUN 39 removed that restriction at the Technical Delegate's own request — *"remove the
   * restriction … allowing the TD to update these to play around with different
   * combination"* — because it was never policy: it was a seam in how a per-slot write's reply
   * was landed, and the seam is closed in `AppointmentsPanel` (`landSlotView`).
   *
   * So this day is no longer a permission. It is a LANDMARK, and one thing depends on it: the
   * separator carries an `Appointing` tag, because the day-warning counts, the `N of 18 slots
   * filled` line and `Confirm Day` still mean this day and only this day, whichever row was
   * last edited.
   *
   * `null` — as in every test that renders this panel on its own — means no day is marked.
   */
  workingDay?: string | null;
  warnings: readonly AppointmentWarning[];
  officials: readonly OfficialView[];
  /**
   * True once the whole-schedule read has answered. Until it does, this list holds the working
   * day alone; when it flips, every earlier day appears above the day the TD was landed on, so
   * the pane must be landed once more (see the view key below).
   */
  scheduleArrived: boolean;
  competition: CompetitionFilter;
  onCompetition: (value: CompetitionFilter) => void;
  stage: StageFilter;
  onStage: (value: StageFilter) => void;
  busy: boolean;
  drafting: boolean;
  /** True while the stage draft is running — the whole panel is busy meanwhile. */
  draftingStage: boolean;
  /** True while `Clear Drafts` is running. */
  clearing: boolean;
  confirming: boolean;
  onDraft: () => void;
  /** §A8's one press for the whole stage. Opens the confirmation, never drafts directly. */
  onDraftStage: () => void;
  /** RUN 38 — the same stage draft, shuffled. Beside its sibling, in the same visual family. */
  onReDraft: () => void;
  redrafting: boolean;
  /** What that press is called — `Draft The Pool Stage`, or the wider fallback. */
  stageLabel: string;
  /** The rehearsal control. Opens the question; it never clears directly. */
  onClearDrafts: () => void;
  onConfirm: () => void;
  /** RUN 82 — `Mark Day Locked` is running (§A11). */
  locking: boolean;
  /**
   * RUN 82 — the press has nothing left to do on the WORKING day, so it says `Day Locked` instead.
   * Worked out above from the day view, because the day the press covers is the day the Match Day
   * dropdown names and not whatever this list shows.
   *
   * RUN 82A — "nothing left to do" and not "every match locked": a match TMS marked Official
   * while it was not confirmed here is one the lock skips, and a day holding one would otherwise
   * keep a live button that could never finish. See `nothingLeftToLock`.
   */
  dayLocked: boolean;
  /**
   * RUN 82A — nothing is left to lock AND nothing on the day is locked: every match is Official in
   * TMS and not confirmed here. The press is as finished as on a locked day, but `Day Locked`
   * would claim a record nobody made, so it says `Nothing To Lock` instead.
   */
  nothingToLock: boolean;
  /** RUN 82 — opens the `Mark Day Locked` question. It never locks directly. */
  onLock: () => void;
  /** RUN 82 — opens the `Unlock` question for one match. It never unlocks directly. */
  onUnlock: (fixture: AppointmentFixtureBody) => void;
  /** RUN 82 — the TMS database id of the match whose unlock is running, or null. */
  unlocking: number | null;
  /**
   * RUN 84 - the day's push controls, one per competition (`Push Men's To TMS`, ...), drawn by the
   * panel above, which owns the press. They sit straight after `Mark Day Locked`, with the day's
   * other presses. Absent in a test that renders this panel on its own.
   */
  pushActions?: React.ReactNode;
  /** §A10 — the day as displayed, as an `.xlsx`. Reads state; writes nothing. */
  onExport: () => void;
  exporting: boolean;
  onCheck: (fixture: AppointmentFixtureBody) => void;
  /** Which slot the picker is open on, so the row can mark it as expanded. */
  openSlot: { tmsMatchDbId: number; slot: TechnicalSlot } | null;
  onOpenPicker: (
    fixture: AppointmentFixtureBody,
    slot: AppointmentSlotBody,
    anchor: HTMLElement,
  ) => void;
  onWithdraw: (fixture: AppointmentFixtureBody, slot: AppointmentSlotBody) => void;
  /** The one slot pulsing after a failed `Check`, as §A7 describes. */
  pulsing: string | null;
  savingRow: string | null;
  slotError: { row: string; message: string } | null;
  /** The fixture the warning counts scrolled the TD to, so the row can be marked. */
  highlighted: number | null;
  /** Warning codes the day summary is filtering to, or null for all. */
  onWarningFilter: (severity: "hard" | "attention" | "advisory" | "anomaly" | null) => void;
  /**
   * The day-warning strip, rendered inside this panel's own sticky head (F-20).
   *
   * It is passed in rather than built here because the counts and the narrowing they drive
   * belong to the panel above; what F-20 changed is only *where* it sits. On a wide screen
   * the fixture list is a pane that scrolls inside itself, and a warning strip that scrolled
   * away with the rows would be a filter the TD could no longer switch off.
   */
  daySummary?: React.ReactNode;
}

export function rowKey(tmsMatchDbId: number, slot: TechnicalSlot): string {
  return `${String(tmsMatchDbId)}:${slot}`;
}

/**
 * RUN 82A — the DOM id of `Mark Day Locked`, so its question can hand keyboard focus back to it
 * (WCAG 2.4.3).
 *
 * An id rather than a ref because the question lives in the panel above and the button lives
 * here: a ref threaded down for one focus call would be a prop that exists for nothing else. One
 * constant, read by both sides, so the two can never name different elements.
 */
export const LOCK_DAY_ID = "lockDay";

/** RUN 82A — the DOM id of one match's `Unlock`, for the same hand-back. Keyed on the TMS id. */
export function unlockButtonId(tmsMatchDbId: number): string {
  return `unlock-${String(tmsMatchDbId)}`;
}

/**
 * RUN 82G (QA pass 3) — the DOM ids the page's OLDER questions hand keyboard focus back to (WCAG
 * 2.4.3), for the reason `LOCK_DAY_ID` gives. `Cancel` gives it back to the press that asked; an
 * answer that changes one slot — a pick, a withdrawal, an override — gives it to that slot's name,
 * so a keyboard TD is left in the row he was working on rather than at the top of the page.
 */
export const DRAFT_STAGE_ID = "draftStage";
export const REDRAFT_ID = "redraftStage";
export const CLEAR_DRAFTS_ID = "clearDrafts";

/** RUN 82G — one slot's `Withdraw`. */
export function withdrawButtonId(tmsMatchDbId: number, slot: string): string {
  return `withdraw-${String(tmsMatchDbId)}-${slot}`;
}

/**
 * RUN 82G — one slot's name: the button that opens the officials popover.
 *
 * RUN 82I (QA pass 4) — the button only. 82G also put it, focusable, on the plain text of a slot
 * that can no longer change; a click then left that text holding the focus, and its row tinted
 * (`tr:focus-within`), and a screen reader heard the lock reason twice — once as the focusable
 * text's name, once as the hidden sentence beside it. A hand-off aimed at a slot that has just
 * become read-only goes to the refusal on that slot instead ({@link slotErrorId}).
 */
export function slotNameId(tmsMatchDbId: number, slot: string): string {
  return `slotname-${String(tmsMatchDbId)}-${slot}`;
}

/**
 * RUN 82I (QA pass 4) — the refusal drawn on one slot, which takes the focus (without joining the
 * tab order) when the answer it refused has left the slot with no name to return to.
 *
 * RUN 82K (QA pass 5) — it is focusable only while the focus is handed to it: the panel gives it
 * `tabindex="-1"` just before, and takes it away again on blur. Carried permanently, a click on
 * the refusal's words left its row tinted (`tr:focus-within`), as a click on a read-only name had.
 */
export function slotErrorId(tmsMatchDbId: number, slot: string): string {
  return `sloterror-${String(tmsMatchDbId)}-${slot}`;
}

/*
 * The foot's tips fold moved to `TipsFold.tsx` the day the Tech Table asked for the same
 * treatment — one component, per-panel storage keys. Why the foot folds and nothing else on
 * the panel does: the Technical Delegate works a day at a time and a day is six matches; the
 * foot's two paragraphs were costing him the sixth. Nothing live is in there — the day-warning
 * counts, the advisory line, every per-row anomaly note and every refusal stay open, because a
 * warning the TD has to open something to see is a warning that has been hidden.
 */

export function FixtureListPanel({
  fixtures,
  allFixtures,
  landOn = null,
  workingDay = null,
  warnings,
  officials,
  scheduleArrived,
  competition,
  onCompetition,
  stage,
  onStage,
  busy,
  drafting,
  draftingStage,
  clearing,
  confirming,
  onDraft,
  onDraftStage,
  onReDraft,
  redrafting,
  onClearDrafts,
  stageLabel,
  onConfirm,
  locking,
  dayLocked,
  nothingToLock,
  onLock,
  onUnlock,
  unlocking,
  pushActions = null,
  onExport,
  exporting,
  onCheck,
  openSlot,
  onOpenPicker,
  onWithdraw,
  pulsing,
  savingRow,
  slotError,
  highlighted,
  daySummary,
}: FixtureListPanelProps): React.JSX.Element {
  /* RUN 37 — the pane the whole schedule scrolls inside, and the day it opens at.
     The two filters are the view key: each of them takes rows out from ABOVE the anchor, so
     the day the TD is reading slides up the pane unless the pane is landed again. Nothing
     else re-lands it — an edit, a draft or a new warning leaves the scroll where he put it.

     `scheduleArrived` is the third part, and it is the whole of a defect found on 20 Sep:
     the page draws this panel as soon as the DAY read answers, so the first paint holds
     today's rows alone, the pane lands on them (a no-op — there is nothing above them) and
     the landing is spent. The whole-schedule read then answers and inserts every earlier day
     ABOVE today, leaving the TD on Day 1 while `data-scrolled-to` still says today. Flipping
     this flag once, when the backdrop arrives, buys exactly one more landing. It is NOT the
     set of days drawn: a Day Warnings narrowing changes that too, and must never move him. */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useLandOnDay(scrollRef, landOn, `${competition}|${stage}|${scheduleArrived ? "whole" : "day"}`);

  const groups = dayGroups(fixtures);
  const numbers = dayNumbers(allFixtures.map((fixture) => fixture.date));

  return (
    <section className="panel panel--d1" aria-labelledby="fixListTitle" data-howto="fixture-list">
      <div className="panel__stick">
      <div className="panel__head">
        <h3 className="panel__title" id="fixListTitle">
          Fixture List
        </h3>
        <button
          type="button"
          className="gbtn gbtn--solid"
          data-howto="draft-appointments"
          onClick={onDraft}
          disabled={busy}
          aria-busy={drafting}
        >
          {drafting ? "Drafting…" : "Draft Appointments"}
        </button>
        {/* §A8 — *"the engine drafts all pool matches up front, before the tournament"*. The
            single-day button beside it is unchanged and still the way a knockout day is
            drafted; this one crosses days whichever day is on screen, which is why it asks
            first. */}
        <button
          type="button"
          className="gbtn gbtn--solid"
          id={DRAFT_STAGE_ID}
          data-howto="draft-stage"
          onClick={onDraftStage}
          disabled={busy}
          aria-busy={draftingStage}
        >
          {draftingStage ? "Drafting The Stage…" : stageLabel}
        </button>
        {/* RUN 38 — the shuffle. Same visual family as the button beside it because it IS that
            button, with the engine's tie-break rotation moved on one: a different arrangement,
            equally even and equally legal, with every confirmed, hand-edited and pushed slot
            kept exactly as the plain stage draft keeps them. It asks first, for the same reason
            its sibling does — it crosses days. */}
        <button
          type="button"
          className="gbtn gbtn--solid"
          id={REDRAFT_ID}
          data-howto="redraft-stage"
          data-testid="redraft-stage"
          onClick={onReDraft}
          disabled={busy}
          aria-busy={redrafting}
          title="Draft the stage again, arranged differently. Confirmed and hand-edited slots are kept."
        >
          {redrafting ? "Re-Drafting…" : "Re-Draft"}
        </button>
        <button
          type="button"
          className="gbtn"
          data-howto="confirm-day"
          onClick={onConfirm}
          disabled={busy}
          aria-busy={confirming}
        >
          {confirming ? "Confirming…" : "Confirm Day"}
        </button>
        {/* RUN 82 (§A11) — the step after the sign-off, so it sits straight after it and in the
            same family: a statement about the day, not a draft. It is NOT disabled merely
            because the day is unconfirmed — the server names every match that is not ready,
            exactly as it does for `Confirm Day`, and a disabled button explains nothing. It is
            disabled when there is nothing left for it to do, and then it says so. */}
        {/* RUN 82A — the title under `Day Locked` says what the button can actually know: the
            screen cannot see TMS, and an Official match nobody confirmed is skipped by the lock
            rather than locked by it, so "every match is locked" would be untrue on that day.

            RUN 82D — "not confirmed here", no longer "never": a match reopened with Unlock was
            confirmed once, and is skipped just the same if TMS marks it Official before it is
            confirmed again. */}
        <button
          type="button"
          className="gbtn"
          id={LOCK_DAY_ID}
          data-testid="lock-day"
          data-howto="lock-day"
          onClick={onLock}
          disabled={busy || dayLocked}
          aria-busy={locking}
          title={
            dayLocked
              ? "Nothing left to lock on this day: every match is locked, or is Official in TMS and not confirmed here"
              : "Press once this day's TO, SJ and TJ are entered and published in TMS"
          }
        >
          {locking
            ? "Locking…"
            : nothingToLock
              ? "Nothing To Lock"
              : dayLocked
                ? "Day Locked"
                : "Mark Day Locked"}
        </button>
        {/* RUN 84 - the push, one competition at a time: TMS saves a competition's day in one post,
            so the control is per competition, on the head, and never on a row. */}
        {pushActions}
        {/* Deliberately the quietest control on the head: it is the only one that removes
            work, it is for rehearsal rather than for the tournament, and it must never be
            the thing a hand reaches for by accident on a match morning. It still asks. */}
        <button
          type="button"
          className="gbtn gbtn--quiet"
          id={CLEAR_DRAFTS_ID}
          data-howto="clear-drafts"
          onClick={onClearDrafts}
          disabled={busy}
          aria-busy={clearing}
        >
          {clearing ? "Clearing…" : "Clear Drafts"}
        </button>
        {/* §A3 reserves Export for the pager row; this list has no pager, so it sits on the
            head beside the other whole-list controls. It is not a write and is deliberately
            not disabled while one is running — a TD may take a copy of the day at any point,
            and what he gets is what is in front of him. */}
        <button
          type="button"
          className="gbtn"
          data-testid="export-technical"
          onClick={onExport}
          aria-busy={exporting}
          disabled={exporting}
          title="Download the fixture appointments as displayed, with a timestamp"
        >
          {exporting ? "Exporting…" : "Export"}
        </button>
        <span className="panel__note">The Engine Drafts The Day, Then You Adjust</span>
        <span className="spacer" />
        <span className="filtlab">Competition</span>
        <Tabs
          label="Competition filter"
          options={COMPETITION_TABS}
          value={competition}
          onChange={onCompetition}
          tint="competition"
        />
        <span className="filtlab">Stage</span>
        <Tabs label="Stage filter" options={STAGE_TABS} value={stage} onChange={onStage} />
      </div>
      {daySummary}
      </div>

      <div className="fixwrap fixwrap--all" ref={scrollRef} data-testid="fixlist-scroll">
        <table className="mtable fixtable" aria-label="Fixture list with technical appointments">
          <thead>
            <tr>
              <th scope="col">Match</th>
              <th scope="col">Date</th>
              <th scope="col">TO</th>
              <th scope="col">SJ</th>
              <th scope="col">TJ</th>
              <th scope="col">Status</th>
              <th scope="col">TMS Status</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {/* RUN 37 — one block per match day, each under its own separator row. The
                separator is the landmark a fortnight-long scroll needs, and it is also the
                thing the pane scrolls itself to on the first draw. */}
            {groups.map((group) => (
              <Fragment key={group.date}>
                <tr
                  className={
                    group.date === workingDay ? "fixdayrow fixdayrow--working" : "fixdayrow"
                  }
                  data-testid="fixlist-dayrow"
                  data-day={group.date}
                >
                  <td colSpan={8}>
                    <span className="fixdayrow__date">{dayLabel(group.date)}</span>
                    {numbers.get(group.date) === undefined ? null : (
                      <span className="fixdayrow__no">
                        — Day {numbers.get(group.date)}
                      </span>
                    )}
                    <span className="fixdayrow__count num">
                      {group.fixtures.length} match{group.fixtures.length === 1 ? "" : "es"}
                    </span>
                    {group.date === workingDay ? (
                      <span className="fixdayrow__tag" data-testid="fixlist-workingday">
                        Appointing
                      </span>
                    ) : null}
                  </td>
                </tr>

                {group.fixtures.map((fixture) => (
                  <FixtureRow
                    key={fixture.tmsMatchDbId}
                    fixture={fixture}
                    warnings={warnings.filter(
                      (warning) => warning.tms_match_db_id === fixture.tmsMatchDbId,
                    )}
                    officials={officials}
                    openSlot={openSlot}
                    onOpenPicker={onOpenPicker}
                    onWithdraw={onWithdraw}
                    onCheck={onCheck}
                    onUnlock={onUnlock}
                    unlocking={unlocking === fixture.tmsMatchDbId}
                    pulsing={pulsing}
                    savingRow={savingRow}
                    slotError={slotError}
                    highlighted={highlighted === fixture.tmsMatchDbId}
                    busy={busy}
                  />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>

        {fixtures.length === 0 ? (
          <div className="emptystate" data-testid="fixtures-empty">
            <b>
              {allFixtures.length === 0
                ? "No Matches In The Schedule"
                : "No Matches In This View"}
            </b>
            {allFixtures.length === 0
              ? "Nothing is scheduled, so there is nothing to appoint. Load the fixtures first."
              : "Every match in the schedule sits outside the filters above. Widen them to see it."}
          </div>
        ) : null}
      </div>

      <div className="panel__foot">
        <TipsFold storageKey="hockeytd.tips.fixture-list" testId="fixture-tips">
        <b>Conflict hint —</b> a name shown in red failed the quick check: the official is
        appointed to a match involving their own country. That is a hard-line rule; clear it by
        choosing another name, or keep your choice and override it — two deliberate steps, your
        name recorded, and the slot stays marked afterwards. Impossible violations — the same
        official in two roles in one match — have no override at all.
        <div className="hidenote">
          {/* RUN 84 - it said there was no push in this build, which the push controls on the head
              now contradict. What is still true is kept: there is no Push on a row. */}
          <span data-testid="push-foot-note">
            <b>No Push button on the rows &mdash;</b> TMS saves a competition&rsquo;s whole day in
            one post, so the push is on the head, one control per competition. It goes through the
            TMS Push extension in this browser, and nothing reaches TMS until you confirm it again
            in the extension&rsquo;s own window. A match TMS accepts is locked here, unless its crew
            changed while the push was out or the result came after the day&rsquo;s hold ran out
            &mdash; the answer says so for each such match.
          </span>{" "}
          Umpire slots are untouched throughout: U1, U2, RU and VU belong to the Umpires
          Managers.{" "}
          {/* RUN 82 (§A11) — with no push, the lock is how a day keyed into TMS by hand is
              recorded here, so the sentence sits beside the one that says there is no push. */}
          <span data-testid="lock-foot-note">
            <b>Mark Day Locked —</b> press it once the day is entered and published in TMS by
            hand; locked appointments are read-only here, and a match reopened with{" "}
            <b>Unlock</b> must be corrected in TMS and the day locked again.
          </span>
        </div>
        </TipsFold>
      </div>
    </section>
  );
}

/* ── One fixture ──────────────────────────────────────────────────────────── */

interface FixtureRowProps {
  fixture: AppointmentFixtureBody;
  warnings: readonly AppointmentWarning[];
  officials: readonly OfficialView[];
  openSlot: { tmsMatchDbId: number; slot: TechnicalSlot } | null;
  onOpenPicker: (
    fixture: AppointmentFixtureBody,
    slot: AppointmentSlotBody,
    anchor: HTMLElement,
  ) => void;
  onWithdraw: (fixture: AppointmentFixtureBody, slot: AppointmentSlotBody) => void;
  onCheck: (fixture: AppointmentFixtureBody) => void;
  /** RUN 82 — opens the §A11 Unlock question for this match. */
  onUnlock: (fixture: AppointmentFixtureBody) => void;
  /** RUN 82 — this match's unlock is the write in flight. */
  unlocking: boolean;
  pulsing: string | null;
  savingRow: string | null;
  slotError: { row: string; message: string } | null;
  highlighted: boolean;
  busy: boolean;
}

/**
 * The fixture's roll-up status pill.
 *
 * The five statuses are per-slot in the store, and the row shows one. The order below is
 * "what the TD needs to act on first": an unresolved breach outranks an override, which
 * outranks a hand edit, and a fixture nobody has touched yet says exactly that.
 *
 * **Exported because §A10's `Status` column is this same roll-up.** The export reflects what is
 * displayed, and the surest way to keep a sheet agreeing with the row it was taken from is for
 * there to be one function that decides what the row says.
 *
 * **RUN 82 — two states that are about TMS rather than about the draft (§A11).** `On TMS` is a
 * match whose three slots are locked: the record of who officiated, which is why it outranks an
 * override and a sign-off, both of which it stands on. `Needs Re-Push` is a match reopened for a
 * correction that TMS does not hold yet, and it outranks the same two for the reason the TD
 * reads this column at all — it is the one with work still owed OUTSIDE this screen. An
 * unresolved breach still outranks both: a breach on a locked match is the thing to see first.
 * `icon` is the pill's glyph where it carries one instead of the dot; the export reads `label`
 * alone, so the sheet says `On TMS` and never a padlock.
 */
function fixtureStatus(fixture: AppointmentFixtureBody): {
  label: string;
  className: string;
  icon?: string;
} {
  const slots = fixture.slots;
  if (slots.some((slot) => hasRuleBreach(slot) && slot.status !== "overridden")) {
    return { label: "Error", className: "statuspill statuspill--alert" };
  }
  if (fixture.locked) {
    return { label: "On TMS", className: "statuspill statuspill--ontms", icon: "🔒" };
  }
  if (fixture.needsPush) {
    return { label: "Needs Re-Push", className: "statuspill statuspill--warn statuspill--repush" };
  }
  if (slots.some((slot) => slot.status === "overridden")) {
    return { label: "Overridden", className: "statuspill statuspill--warn" };
  }
  if (fixture.confirmed) return { label: "Confirmed", className: "statuspill statuspill--good" };
  if (slots.some((slot) => slot.status === "tms_pushed")) {
    return { label: "TMS Pushed", className: "statuspill statuspill--good" };
  }
  if (slots.some((slot) => slot.status === "td_modified")) {
    return { label: "TD Modified", className: "statuspill statuspill--warn" };
  }
  if (slots.some((slot) => slot.status === "ai_drafted")) {
    return { label: "AI Drafted", className: "statuspill" };
  }
  return { label: "Unfilled", className: "statuspill statuspill--muted" };
}

function fixtureTms(fixture: AppointmentFixtureBody): string {
  if (fixture.slots.some((slot) => slot.tmsStatus === "published")) return "Published";
  if (fixture.slots.some((slot) => slot.tmsStatus === "draft")) return "Draft";
  return "—";
}

/*
 * RUN 39 — what used to live here, and why it does not any more.
 *
 * RUN 37 made every row off the working day read-only; RUN 38 gave those rows a click-note
 * (`offDayReason`, `OFF_DAY_NOTE_MS`, a per-row `telling` state) so that a refusal in silence
 * could at least be told apart from a broken control. The Technical Delegate then asked for
 * the restriction itself to go — *"allowing the TD to update these to play around with
 * different combination"* — so there is no refusal left to explain, and the note, its timer
 * and its state are gone rather than repurposed. A sentence that can no longer be true is
 * worse than no sentence.
 */

function FixtureRow({
  fixture,
  warnings,
  officials,
  openSlot,
  onOpenPicker,
  onWithdraw,
  onCheck,
  onUnlock,
  unlocking,
  pulsing,
  savingRow,
  slotError,
  highlighted,
  busy,
}: FixtureRowProps): React.JSX.Element {
  const status = fixtureStatus(fixture);
  const signOff = fixtureSignOff(fixture);

  const rowClasses = [
    fixture.frozen ? "is-frozen" : "",
    highlighted ? "is-highlighted" : "",
  ]
    .filter((entry) => entry !== "")
    .join(" ");

  return (
    <>
      <tr
        data-comp={fixture.competitionGender}
        data-testid={`fixture-${fixture.tmsMatchDbId}`}
        className={rowClasses === "" ? undefined : rowClasses}
      >
        <td className="cell-match">
          <span
            className={`comp-tag${fixture.competitionGender === "w" ? " comp-tag--w" : ""}`}
          >
            {fixture.competitionGender === "w" ? "Women" : "Men"}
          </span>
          <span className="mno num">{fixture.displayMatchNo}</span>
          <span className="teams">
            {fixture.homeTeamCode ?? "TBC"} <i>v</i> {fixture.awayTeamCode ?? "TBC"}
          </span>
        </td>
        <td className="cell-date">
          {fixture.date}
          <span className="tm num">
            {fixture.time}
            {fixture.pitch === null ? "" : ` · ${fixture.pitch}`}
          </span>
        </td>

        {fixture.slots.map((slot) => (
          <SlotCell
            key={slot.slot}
            fixture={fixture}
            slot={slot}
            officials={officials}
            open={
              openSlot?.tmsMatchDbId === fixture.tmsMatchDbId && openSlot.slot === slot.slot
            }
            onOpenPicker={onOpenPicker}
            onWithdraw={onWithdraw}
            /* F-19 — the sign-off is stated once on the fixture above. A slot only carries
               its own chip where it disagrees with its neighbours. */
            showSignOff={!signOff.uniform}
            pulsing={pulsing === rowKey(fixture.tmsMatchDbId, slot.slot)}
            saving={savingRow === rowKey(fixture.tmsMatchDbId, slot.slot)}
            error={
              slotError?.row === rowKey(fixture.tmsMatchDbId, slot.slot)
                ? slotError.message
                : null
            }
            busy={busy}
          />
        ))}

        <td className="cell-status">
          <span className={status.className} data-testid={`fixture-status-${fixture.tmsMatchDbId}`}>
            {status.icon === undefined ? (
              <span className="sdot" aria-hidden="true" />
            ) : (
              <span className="slock" aria-hidden="true">
                {status.icon}
              </span>
            )}
            {status.label}
          </span>
          {/* F-19 — sign-off and lock, once per fixture and compact. The exact instant the
              API sent stays on the title and on `data-confirmed-at`; the chip itself is read
              on the tournament's clock, which is the one the TD is working to.

              F-21 — and the two of them share ONE line, right-aligned. Stacking them was what
              made a signed-off fixture a four-line row: *"very tall with a lot of empty space
              … because of the two status boxes"*. */}
          {signOff.at === null && signOff.lock === null && !signOff.needsPush ? null : (
            <span className="fixchips">
              {signOff.at === null ? null : (
                <span
                  className="fixchip fixchip--good"
                  data-testid={`fixture-confirmed-${fixture.tmsMatchDbId}`}
                  data-confirmed-at={signOff.at}
                  title={`Signed off ${exactTournamentInstant(signOff.at)}`}
                >
                  Confirmed {shortTournamentTime(signOff.at)}
                </span>
              )}
              {/* RUN 82 (§A11) — where a signed-off match says `Signed Off`, a locked one says
                  what it is now: the record TMS holds, and when it became that. The instant is
                  kept whole on the title and on `data-locked-at`, as the sign-off's is. */}
              {signOff.lock !== "on_tms" ? null : (
                <span
                  className="fixchip fixchip--ontms"
                  data-testid={`fixture-ontms-${fixture.tmsMatchDbId}`}
                  {...(signOff.lockedAt === null
                    ? {}
                    : {
                        "data-locked-at": signOff.lockedAt,
                        title: `Locked ${exactTournamentInstant(signOff.lockedAt)}`,
                      })}
                >
                  <span aria-hidden="true">🔒</span> On TMS · Locked
                  {signOff.lockedAt === null ? "" : ` ${tournamentClock(signOff.lockedAt)}`}
                </span>
              )}
              {/* RUN 82A — `Signed Off` and `Official in TMS`, no longer `Locked · …`: since §A11
                  gave `Locked` its own meaning, a signed-off match that said `Locked` sat beside
                  a `Mark Day Locked` press that was still live. The test ids are unchanged. */}
              {signOff.lock === null || signOff.lock === "on_tms" ? null : (
                <span
                  className="fixchip fixchip--lock"
                  data-testid={
                    signOff.lock === "official"
                      ? `fixture-frozen-${fixture.tmsMatchDbId}`
                      : `fixture-locked-${fixture.tmsMatchDbId}`
                  }
                >
                  {signOff.lock === "official" ? "Official in TMS" : "Signed Off"}
                </span>
              )}
              {/* RUN 82 — the correction still owed outside this screen. It stands whatever the
                  pill says, because a breach outranks it there and the debt is still owed.

                  RUN 82A — its tooltip is where a reopened match says, once, why its slots offer
                  no Withdraw; each slot keeps that sentence for a screen reader only. The
                  `title` is also the chip's accessible description.

                  RUN 82D (QA pass 2) — a tooltip on a span that takes no focus reaches a mouse and
                  nobody else, so the note is also read out inside the chip. And a match reopened,
                  then marked Official before it was confirmed again, says what it now is instead
                  of sending the TD to two presses that can no longer settle it.

                  RUN 82G (QA pass 3) — and it is read out ONCE. The `title` on the chip was still
                  its accessible description, so a screen reader heard the sentence twice: as the
                  chip's text and again as its description. The tooltip now sits on the two
                  printed words, hidden from assistive technology, and the chip's whole reading —
                  the two words and the sentence — is the one hidden-from-sight span. */}
              {!signOff.needsPush ? null : (
                <span
                  className="fixchip fixchip--repush"
                  data-testid={`fixture-repush-${fixture.tmsMatchDbId}`}
                >
                  <span aria-hidden="true" title={needsRePushNote(fixture)}>
                    Needs Re-Push
                  </span>
                  {/* The full stop parts the chip's two words from the sentence after them, which a
                      screen reader would otherwise run together. */}
                  <span
                    className="sr-only"
                    data-testid={`fixture-repush-note-${fixture.tmsMatchDbId}`}
                  >
                    {`Needs Re-Push. ${needsRePushNote(fixture)}`}
                  </span>
                </span>
              )}
            </span>
          )}
        </td>

        <td className="num">{fixtureTms(fixture)}</td>

        <td>
          <span className="rowact">
            <button
              type="button"
              onClick={() => {
                onCheck(fixture);
              }}
            >
              Check
            </button>
            {/* RUN 82 (§A11) — a correction is one match at a time, so the way out of a lock is
                on the match's own row. Quiet, because it undoes a record rather than making one,
                and absent on an Official match, which the API will not reopen. It asks first. */}
            {!fixture.locked || fixture.frozen ? null : (
              <button
                type="button"
                className="rowact__quiet"
                id={unlockButtonId(fixture.tmsMatchDbId)}
                data-testid={`unlock-${fixture.tmsMatchDbId}`}
                disabled={busy}
                aria-busy={unlocking}
                onClick={() => {
                  onUnlock(fixture);
                }}
              >
                {unlocking ? "Unlocking…" : "Unlock"}
                <span className="sr-only"> {fixture.displayMatchNo}</span>
              </button>
            )}
          </span>
        </td>
      </tr>

      {warnings.length === 0 ? null : (
        <tr className="warnrow" data-testid={`fixture-warnings-${fixture.tmsMatchDbId}`}>
          <td colSpan={8}>
            <FixtureWarnings warnings={warnings} officials={officials} />
          </td>
        </tr>
      )}
    </>
  );
}

/* ── One slot ─────────────────────────────────────────────────────────────── */

interface SlotCellProps {
  fixture: AppointmentFixtureBody;
  slot: AppointmentSlotBody;
  officials: readonly OfficialView[];
  open: boolean;
  onOpenPicker: (
    fixture: AppointmentFixtureBody,
    slot: AppointmentSlotBody,
    anchor: HTMLElement,
  ) => void;
  onWithdraw: (fixture: AppointmentFixtureBody, slot: AppointmentSlotBody) => void;
  /** True only where this slot's sign-off differs from its neighbours' (F-19). */
  showSignOff: boolean;
  pulsing: boolean;
  saving: boolean;
  error: string | null;
  busy: boolean;
}

function SlotCell({
  fixture,
  slot,
  officials,
  open,
  onOpenPicker,
  onWithdraw,
  showSignOff,
  pulsing,
  saving,
  error,
  busy,
}: SlotCellProps): React.JSX.Element {
  /* RUN 39 — the ONLY locks left are the ones §A4 and CC-Q25 impose: a fixture TMS has marked
     Official, and a slot the TD has signed off. Which day the match falls on has stopped being
     one of them, so every slot on every day draws the same `.slotbtn` control.

     RUN 82 — and the one §A11 imposes: a slot locked as the record TMS holds. It is drawn as a
     signed-off slot is, as plain text that carries its reason. */
  const lock: { editable: boolean; reason: string | null } = slotLock(fixture, slot);
  const withdrawal = withdrawLock(fixture, slot);
  const breach = hasRuleBreach(slot);
  const testId = `slot-${String(fixture.tmsMatchDbId)}-${slot.slot}`;
  // The name takes the official's gender colour, exactly as the roster and the matrices do.
  // Where the roster could not be loaded there is no colour to take, and the name is plain.
  const gender =
    officials.find((official) => official.tmsPersonId === slot.tmsPersonId)?.gender ?? null;

  const nameClasses = [
    "slotbtn",
    gender === null ? "" : `g-${gender}`,
    breach && slot.status !== "overridden" ? "is-bad" : "",
    slot.status === "overridden" ? "is-overridden" : "",
    pulsing ? "pulse" : "",
    saving ? "is-saving" : "",
  ]
    .filter((entry) => entry !== "")
    .join(" ");

  const label =
    slot.mediaName === null
      ? `Appoint an official to the ${fixture.displayMatchNo} ${slot.slot} slot`
      : `Change ${slot.mediaName} in the ${fixture.displayMatchNo} ${slot.slot} slot`;

  return (
    <td className="slotcell" data-testid={testId}>
      {lock.editable ? (
        <button
          type="button"
          className={slot.mediaName === null ? "slotbtn is-empty" : nameClasses}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={label}
          id={slotNameId(fixture.tmsMatchDbId, slot.slot)}
          data-testid={`slot-name-${String(fixture.tmsMatchDbId)}-${slot.slot}`}
          disabled={busy && !saving}
          /* RUN 82I (QA pass 4) — the saving row's name stays enabled, so the focus is not thrown
             off it, but a press does nothing while its write is out; it says so. */
          aria-disabled={saving || undefined}
          aria-busy={saving || undefined}
          onClick={(event) => {
            onOpenPicker(fixture, slot, event.currentTarget);
          }}
        >
          <span className="nm">{slot.mediaName ?? "Unfilled"}</span>
          <span className="nat num">{slot.country ?? "Choose an official"}</span>
        </button>
      ) : (
        /* A locked slot still says why it is locked — F-19 only took the sentence off the
           face of the row, where three copies of it per fixture made the list unreadable.
           It stays on the name's own tooltip, and in the lock line below, which is now
           read out rather than printed. */
        /* RUN 82K (QA pass 5) — the tooltip is on the printed name and country, which a screen
           reader skips, and the name is read from a hidden copy: a `title` on the name itself was
           its accessible description, and the same reason is already read out once, just below
           (`slot-lock-*`). The pattern the `Needs Re-Push` chip took in RUN 82G. */
        <span className={nameClasses} data-testid={`slot-name-${String(fixture.tmsMatchDbId)}-${slot.slot}`}>
          <span className="nm" aria-hidden="true" title={lock.reason ?? undefined}>
            {slot.mediaName ?? "Unfilled"}
          </span>
          <span className="nat num" aria-hidden="true" title={lock.reason ?? undefined}>
            {slot.country ?? "—"}
          </span>
          <span className="sr-only">
            {slot.country === null
              ? (slot.mediaName ?? "Unfilled")
              : `${slot.mediaName ?? "Unfilled"}, ${slot.country}`}
          </span>
        </span>
      )}

      {/* F-21 — the slot's second line. The breach chip, the override marker, a slot-level
          sign-off, the status word and `Withdraw` used each to open a line of their own,
          which is half of what made the rows *"very tall with a lot of empty space"*. They
          sit beside one another now; the wording of each is untouched. */}
      <span className="slotline">
        {breach ? (
          <span
            className="breachchip"
            title={ruleBreachReason(slot)}
            data-testid={`slot-breach-${String(fixture.tmsMatchDbId)}-${slot.slot}`}
          >
            <span aria-hidden="true">⚠</span> Rule Breach
          </span>
        ) : null}

        {slot.status === "overridden" ? (
          <span
            className="overmark"
            data-testid={`overridden-${String(fixture.tmsMatchDbId)}-${slot.slot}`}
          >
            Overridden
          </span>
        ) : null}

        {slot.confirmedAt === null || !showSignOff ? null : (
          <span
            className="fixchip fixchip--good"
            data-testid={`signoff-${String(fixture.tmsMatchDbId)}-${slot.slot}`}
            data-confirmed-at={slot.confirmedAt}
            title={`Signed off ${exactTournamentInstant(slot.confirmedAt)}`}
          >
            Confirmed {shortTournamentTime(slot.confirmedAt)}
          </span>
        )}

        {slot.status === null || slot.confirmedAt !== null ? null : (
          <span className="slotstatus">{statusLabel(slot.status)}</span>
        )}

        {withdrawal.withdrawable && slot.id !== null ? (
          <button
            type="button"
            className="slotwd"
            id={withdrawButtonId(fixture.tmsMatchDbId, slot.slot)}
            disabled={busy}
            onClick={() => {
              onWithdraw(fixture, slot);
            }}
          >
            Withdraw
          </button>
        ) : null}
      </span>

      {/* F-19 — a locked slot's reason is the fixture's, not the slot's, and the fixture
          states it once in its own row. Kept in the accessibility tree so a reader working
          slot by slot is never told less than the sighted reader is, and never printed
          three times down a column that has no room for it. */}
      {lock.reason === null ? null : (
        <span
          className={lock.editable ? "slotlock" : "sr-only"}
          data-testid={`slot-lock-${String(fixture.tmsMatchDbId)}-${slot.slot}`}
        >
          {lock.reason}
        </span>
      )}

      {/* RUN 82 — only where the slot is otherwise editable. A §A11 lock refuses the withdrawal
          in the same words it refuses the edit, and those words are already on the name.

          RUN 82A — a reopened slot's reason is the MATCH's, exactly as F-19 found a lock's to be:
          the same sentence printed under TO, SJ and TJ. So it is read out here and printed once,
          on the `Needs Re-Push` chip. An overridden or pushed slot's reason is its own and stays
          on the face of the row. */}
      {withdrawal.withdrawable || withdrawal.reason === null || !lock.editable ? null : (
        <span
          className={
            withdrawal.reason === REOPENED_WITHDRAW_REASON ? "sr-only" : "withdrawlock"
          }
          data-testid={`withdraw-lock-${String(fixture.tmsMatchDbId)}-${slot.slot}`}
        >
          {withdrawal.reason}
        </span>
      )}

      {error === null ? null : (
        <span className="rowerror" role="alert" id={slotErrorId(fixture.tmsMatchDbId, slot.slot)}>
          {error}
        </span>
      )}
    </td>
  );
}

/** Exported so the roll-up can be asserted on its own, without rendering a whole day. */
export { fixtureStatus };
